import LabelView from "@components/molecules/LabelView";
import { useTypographyStore } from "@redux/slices/typographySlice";
import { useUserInfo } from "@redux/slices/userInfoSlice";
import currency from "currency.js";
import { useUserCurrency } from "../../../hooks/getCurrency";
import { DefaultTheme } from "styled-components/native";
import ProductCommentView from "./ProductCommentView";
import styled from "styled-components/native";
import { hasZeroDecimals } from "./utils";
import GuestLogin from "./GuestLoginContainer";
import { DefaultThemeValueType } from "../../../../styled";
import { Diamond } from "@src/types/DiamondDetail";
import { PriceModes } from "@components/molecules/PriceModeCard/types";
import { ProductCategory } from "@utils/constants";
import { useAppState } from "@redux/slices/appStateSlice";
import { SpaceType } from "@components/atoms/Spacer/types";
import VDBIcon, { IconsVariant } from "@components/icons/VDBIcon";
import { productLevelOneColor } from "@utils/product/productTheme";
import { VDBIcons } from "@utils/VDBIcons";
import { VDBText } from "@components/atoms";
import CollapsibleDetailView from "@components/molecules/CollapsibleDetailView";
import React, { useState } from "react";
import { isMeleeItem } from "@utils/product/productValidators";

type PDPPriceContainerProps = {
  ref: any;
  isJewelry: boolean;
  isGuest: boolean;
  theme: DefaultTheme;
  t: any;
  productColor: DefaultThemeValueType;
  itemDetail: Diamond;
  priceMode: PriceModes;
  productType: ProductCategory;
};

const PDPPriceContainer = ({
  ref,
  isJewelry,
  isGuest,
  theme,
  t,
  productColor,
  itemDetail,
  priceMode,
  productType,
}: PDPPriceContainerProps) => {
  const typography = useTypographyStore();
  const userCurrency = useUserCurrency();
  const userInfo = useUserInfo();
  const { isCustomApp } = useAppState();
  const hidePriceInPublic =
    userInfo.getFeatureSettings().hide_guest_prices_text;
  const [expandView, setExpandView] = useState(false);

  const renderJewelryContent = () => {
    if (isGuest && hidePriceInPublic) {
      return <GuestLogin />;
    }

    const priceContent = (
      <>
        <LabelView
          itemLeftText={
            isCustomApp
              ? itemDetail.is_finished_product === false
                ? t("productDetailPage.forSettingOnly.text")
                : t("productDetailPage.totalPrice.jewelry.text")
              : t("productDetailPage.totalPrice.text")
          }
          keyWidth={100}
          keyTypographyStyle={typography["Body-R"]}
          valueTypographyStyle={typography["H1-B"]}
          leftIconTheme={theme[productColor as keyof DefaultTheme] as string}
          itemRightText={
            itemDetail?.total_sales_price
              ? currency(itemDetail?.total_sales_price, {
                  symbol: userCurrency,
                  precision:
                    (userInfo?.user?.feature_settings?.round_up_prices &&
                      !isMeleeItem(itemDetail)) ||
                    hasZeroDecimals(itemDetail?.total_sales_price)
                      ? 0
                      : 2,
                }).format()
              : "-"
          }
          rightPrefixTextTheme={theme["core-05"]}
        />
        {itemDetail?.pricing_comment && priceMode !== PriceModes.ORANGE && (
          <>
            <Divider />
            <LabelContainer>
              <VDBIcon
                color={theme[productLevelOneColor(productType)]}
                size={24}
                variant={IconsVariant.VDBCustom}
                name={VDBIcons.PRICE_COMMENT}
              />
              <VDBText
                type={typography["Body-R"]}
                color={theme["core-06"]}
                numberOfLines={expandView ? undefined : 1}
                ellipsizeMode="tail"
              >
                {itemDetail?.pricing_comment}
              </VDBText>
            </LabelContainer>
          </>
        )}
      </>
    );

    if (itemDetail?.pricing_comment && priceMode !== PriceModes.ORANGE) {
      return (
        <CollapsibleDetailView
          expandView={expandView}
          setExpandView={setExpandView}
          initExpanded={expandView}
          title={priceContent}
          arrowStyling={{ color: theme["core-06"] }}
          forceExpand={true}
        >
          <></>
        </CollapsibleDetailView>
      );
    }

    return <PriceContainer ref={ref}>{priceContent}</PriceContainer>;
  };

  const renderProductContent = () => {
    if (isGuest && hidePriceInPublic) {
      return <GuestLogin />;
    }

    return (
      <ProductCommentView
        ref={ref}
        itemDetail={itemDetail}
        productColor={productColor}
        priceMode={priceMode}
        productType={productType}
      />
    );
  };

  return isJewelry ? renderJewelryContent() : renderProductContent();
};

const PriceContainer = styled.View`
  background-color: ${({ theme }) => theme["core-01"]};
  border-color: ${({ theme }) => theme["core-03"]};
  border-width: 1px;
  align-content: flex-start;
  flex-wrap: wrap;
  border-radius: 8px;
  padding: 16px;
`;

const Divider = styled.View`
  height: 1px;
  flex-direction: row;
  background-color: ${({ theme }) => theme["core-03"]};
  width: 100%;
  max-width: 100%;
  margin-top: ${SpaceType.Small}px;
`;

const LabelContainer = styled.View`
  padding-top: 10px;
  padding-bottom: 3px;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: ${SpaceType.Small}px;
  padding-horizontal: ${SpaceType.Basic}px;
`;

export default PDPPriceContainer;
